'use server'

import { apiRequest, ApiResponse } from '../request'

export type GlossStatus =
    | 'GLOSS_PENDING'
    | 'GLOSS_APPEALED'
    | 'GLOSS_ACCEPTED'
    | 'GLOSS_REJECTED'
    | 'GLOSS_PARTIALLY_ACCEPTED'

export type GlossType =
    | 'GLOSS_ADMINISTRATIVE'
    | 'GLOSS_TECHNICAL'
    | 'GLOSS_LINEAR'

export interface Gloss {
    id: number
    companyId: number
    guideId: number
    guide?: {
        id: number
        guideNumber: string
        client?: {
            id: number
            name: string
        }
    }
    healthPlanId: number
    healthPlan?: {
        id: number
        name: string
    }
    itemId?: number
    item?: {
        id: number
        procedureCode: string
        procedureName: string
    }
    glossCode: string
    glossDescription: string
    type: GlossType
    status: GlossStatus
    originalValue: number
    glossValue: number
    recoveredValue?: number
    appealDeadline?: string
    resolvedAt?: string
    notes?: string
    createdAt: string
    updatedAt: string
    appeals?: GlossAppeal[]
    _count?: {
        appeals: number
    }
}

export interface GlossAppeal {
    id: number
    glossId: number
    appealNumber: string
    appealDate: string
    reason: string
    supportingDocuments?: string[]
    status: 'APPEAL_PENDING' | 'APPEAL_SUBMITTED' | 'APPEAL_ACCEPTED' | 'APPEAL_REJECTED'
    responseDate?: string
    responseDescription?: string
    recoveredValue?: number
    createdAt: string
    updatedAt: string
}

export interface GlossCode {
    id: number
    companyId: number
    code: string
    description: string
    type: GlossType
    suggestedAction?: string
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface CreateGlossData {
    guideId?: number
    healthPlanId: number
    itemId?: number
    glossCode: string
    glossDescription: string
    type: GlossType
    originalValue: number
    glossValue: number
    appealDeadline?: string
    notes?: string
}

export interface UpdateGlossData {
    glossDescription?: string
    type?: GlossType
    glossValue?: number
    recoveredValue?: number
    appealDeadline?: string
    notes?: string
}

export interface CreateAppealData {
    reason: string
    supportingDocuments?: string[]
}

export interface GlossFilters {
    healthPlanId?: number
    guideId?: number
    status?: GlossStatus
    type?: GlossType
    startDate?: string
    endDate?: string
}

export interface GlossStats {
    totalGlosses: number
    totalValue: number
    totalRecovered: number
    byStatus: Record<GlossStatus, number>
    byType: Record<GlossType, number>
    recoveryRate: number
}

// Gloss CRUD
export async function getGlosses(
    filters?: GlossFilters
): Promise<ApiResponse<Gloss[]>> {
    return apiRequest<Gloss[]>('billing/glosses', { params: filters })
}

export async function getGloss(id: number): Promise<ApiResponse<Gloss>> {
    return apiRequest<Gloss>(`billing/glosses/${id}`, {})
}

export async function createGloss(
    data: CreateGlossData
): Promise<ApiResponse<Gloss>> {
    return apiRequest<Gloss>('billing/glosses', { data }, 'post')
}

export async function updateGloss(
    id: number,
    data: UpdateGlossData
): Promise<ApiResponse<Gloss>> {
    return apiRequest<Gloss>(`billing/glosses/${id}`, { data }, 'patch')
}

// Appeals
export async function createAppeal(
    glossId: number,
    data: CreateAppealData
): Promise<ApiResponse<Gloss>> {
    return apiRequest<Gloss>(`billing/glosses/${glossId}/appeal`, { data }, 'post')
}

export async function submitAppeal(
    glossId: number,
    appealIdOrText: number | string
): Promise<ApiResponse<Gloss>> {
    // If it's a string, create appeal first, then submit
    if (typeof appealIdOrText === 'string') {
        const createResponse = await createAppeal(glossId, {
            reason: appealIdOrText,
        })
        if (!createResponse.success || !createResponse.result) {
            return createResponse as ApiResponse<Gloss>
        }
        const appealId = createResponse.result.appeals?.[0]?.id
        if (!appealId) {
            return {
                success: false,
                error: 'Erro ao criar recurso',
            } as ApiResponse<Gloss>
        }
        return apiRequest<Gloss>(
            `billing/glosses/${glossId}/appeals/${appealId}/submit`,
            {},
            'post'
        )
    }
    // If it's a number, submit existing appeal
    return apiRequest<Gloss>(
        `billing/glosses/${glossId}/appeals/${appealIdOrText}/submit`,
        {},
        'post'
    )
}

export async function resolveAppeal(
    glossId: number,
    appealId: number,
    data: {
        accepted: boolean
        recoveredValue?: number
        responseDescription?: string
    }
): Promise<ApiResponse<Gloss>> {
    return apiRequest<Gloss>(
        `billing/glosses/${glossId}/appeals/${appealId}/resolve`,
        { data },
        'post'
    )
}

export async function approveAppeal(
    glossId: number,
    appealId: number,
    recoveredValue?: number
): Promise<ApiResponse<Gloss>> {
    return resolveAppeal(glossId, appealId, {
        accepted: true,
        recoveredValue,
    })
}

export async function rejectAppeal(
    glossId: number,
    appealId: number,
    responseDescription?: string
): Promise<ApiResponse<Gloss>> {
    return resolveAppeal(glossId, appealId, {
        accepted: false,
        responseDescription,
    })
}

// Accept gloss (no appeal)
export async function acceptGloss(id: number): Promise<ApiResponse<Gloss>> {
    return apiRequest<Gloss>(`billing/glosses/${id}/accept`, {}, 'post')
}

// Statistics
export async function getGlossStats(filters?: {
    startDate?: string
    endDate?: string
    healthPlanId?: number
}): Promise<ApiResponse<GlossStats>> {
    return apiRequest<GlossStats>('billing/glosses/stats', { params: filters })
}

// Gloss codes
export async function getGlossCodes(
    type?: GlossType
): Promise<ApiResponse<GlossCode[]>> {
    const params = type ? { type } : {}
    return apiRequest<GlossCode[]>('billing/glosses/codes', { params })
}

export async function createGlossCode(data: {
    code: string
    description: string
    type: GlossType
    suggestedAction?: string
}): Promise<ApiResponse<GlossCode>> {
    return apiRequest<GlossCode>('billing/glosses/codes', { data }, 'post')
}

export async function updateGlossCode(
    id: number,
    data: Partial<{
        description: string
        suggestedAction: string
        active: boolean
    }>
): Promise<ApiResponse<GlossCode>> {
    return apiRequest<GlossCode>(`billing/glosses/codes/${id}`, { data }, 'patch')
}

// Labels
export const GLOSS_STATUS_LABELS: Record<GlossStatus, string> = {
    GLOSS_PENDING: 'Pendente',
    GLOSS_APPEALED: 'Em Recurso',
    GLOSS_ACCEPTED: 'Aceita',
    GLOSS_REJECTED: 'Rejeitada',
    GLOSS_PARTIALLY_ACCEPTED: 'Parcialmente Aceita',
}

export const GLOSS_STATUS_COLORS: Record<GlossStatus, string> = {
    GLOSS_PENDING: 'badge-warning',
    GLOSS_APPEALED: 'badge-info',
    GLOSS_ACCEPTED: 'badge-success',
    GLOSS_REJECTED: 'badge-error',
    GLOSS_PARTIALLY_ACCEPTED: 'badge-warning',
}

export const GLOSS_TYPE_LABELS: Record<GlossType, string> = {
    GLOSS_ADMINISTRATIVE: 'Administrativa',
    GLOSS_TECHNICAL: 'Tecnica',
    GLOSS_LINEAR: 'Linear',
}

export const APPEAL_STATUS_LABELS: Record<GlossAppeal['status'], string> = {
    APPEAL_PENDING: 'Pendente',
    APPEAL_SUBMITTED: 'Enviado',
    APPEAL_ACCEPTED: 'Aceito',
    APPEAL_REJECTED: 'Rejeitado',
}

export const APPEAL_STATUS_COLORS: Record<GlossAppeal['status'], string> = {
    APPEAL_PENDING: 'badge-warning',
    APPEAL_SUBMITTED: 'badge-info',
    APPEAL_ACCEPTED: 'badge-success',
    APPEAL_REJECTED: 'badge-error',
}
